/**
 * YKQuiz – klasowy komponent w czystym JS
 * Wymagania spełnione:
 * - Tailwind do stylów
 * - Baner startowy, końcowy oraz banery przypisane do pytań (po pytaniu jeśli istnieją)
 * - COFNIJ (na pytaniach i banerach po pytaniu)
 * - Persistencja w localStorage (auto-wznawianie, reset po ukończeniu)
 * - Postęp na górze (Tailwind)
 * - Pytania: type "text" (lista) lub "visual" (kafelki ze zdjęciem)
 * - Obsługa rozgałęzionych ścieżek quizu
 * - Wynik wyliczany na podstawie wybranej ścieżki i kombinacji odpowiedzi
 */

class YKQuiz extends HTMLElement {
  constructor() {
    super();
    this.root = this;

    this.options = {
      config: null, // obiekt
      src: null, // ścieżka do pliku JSON
      storageKey: 'ykq:state',
      locale: {
        startCta: 'Zaczynamy',
        nextCta: 'NASTĘPNY',
        prevCta: 'POPRZEDNI',
        finishCta: 'Zakończ',
        showResult: 'Pokaż wynik',
      },
      onEnd: null,
    };

    // State
    this.config = null;
    this.mode = 'start'; // 'start' | 'question' | 'between' | 'end'
    this.index = -1; // indeks pytania dla trybu question/between
    this.answers = []; // { questionId, responseId, type }
    this.finalResult = null;
    this.pending = {};
    this._isReady = false;
    this._prefetchedImages = new Set();
  }

  connectedCallback() {
    if (this.options.config && !this._isReady) this.init();
  }

  configure(options = {}) {
    this.options = Object.assign({}, this.options, options);
    if (this.isConnected && this.options.config) this.init();
  }

  async init() {
    this.mode = 'start';
    this.index = -1;
    this.answers = [];
    this.finalResult = null;
    this.pending = {};

    if (this.options.config) {
      this.config = this._normalize(this.options.config);
      this._prefetchImages(this.config);
    } else {
      throw new Error('YKQuiz: provide config or src');
    }

    // Try resume from localStorage
    // const saved = this._loadState();
    const saved = null;
    if (saved) {
      this.mode = saved.mode;
      this.index = saved.index;
      this.answers = saved.answers || [];
      this._recount();
      this._renderCurrent();
    } else {
      this._renderStart();
    }

    this._isReady = true;

    console.log('this.config', this.config);
  }

  // ---------- Persistence ----------
  _saveState() {
    const payload = { mode: this.mode, index: this.index, answers: this.answers };
    try {
      localStorage.setItem(this.options.storageKey, JSON.stringify(payload));
    } catch (e) {}
  }
  _loadState() {
    try {
      const raw = localStorage.getItem(this.options.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }
  _resetState() {
    try {
      localStorage.removeItem(this.options.storageKey);
    } catch (e) {}
  }

  // ---------- Utils ----------
  _normalize(cfg) {
    const def = {
      banners: {
        start: { title: 'Gotowa/y na krótki quiz?', subtitle: '', image: null, cta: this.options.locale.startCta },
        end: { title: 'Dzięki! To już wszystko', subtitle: '', image: null, cta: this.options.locale.showResult },
      },
      questions: [],
      resultRules: [],
    };
    const merged = Object.assign({}, def, cfg);
    merged.questions = (merged.questions || []).map((q) => ({
      id: q.id,
      text: q.text,
      description: q.description,
      image: q.image,
      imageClasses: q.imageClasses || null,
      step: q.step || 1,
      previousQuestionId: q.previousQuestionId || null,
      type: q.type || (q.responses?.some((r) => r.image) ? 'visual' : 'text'),
      banner: q.banner || null,
      responses: (q.responses || []).map((resp) => ({
        ...resp,
        nextQuestionId: resp.nextQuestionId || null,
        resultKey: resp.resultKey || null,
      })),
    }));
    return merged;
  }

  _collectImageUrls(cfg) {
    const urls = [];
    if (!cfg) return urls;

    const addUrl = (url) => {
      if (url && typeof url === 'string') urls.push(url);
    };

    addUrl(cfg?.banners?.start?.image);
    addUrl(cfg?.banners?.end?.image);

    (cfg.questions || []).forEach((q) => {
      addUrl(q.image);
      addUrl(q?.banner?.image);
      (q.responses || []).forEach((resp) => addUrl(resp.image));
    });

    return urls;
  }

  _prefetchImages(cfg) {
    const urls = this._collectImageUrls(cfg);

    urls.forEach((url) => {
      if (!url || this._prefetchedImages.has(url)) return;
      this._prefetchedImages.add(url);

      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      link.fetchPriority = 'low';
      link.crossOrigin = 'anonymous';
      document.head?.appendChild(link);

      const img = new Image();
      img.decoding = 'async';
      img.loading = 'eager';
      img.src = url;
    });
  }

  _getQuestionById(questionId) {
    return this.config.questions.find((question) => question.id === questionId) || null;
  }

  _getQuestionIndexById(questionId) {
    return this.config.questions.findIndex((question) => question.id === questionId);
  }

  _getAnswerMap() {
    return this.answers.reduce((acc, answer) => {
      acc[answer.questionId] = answer.responseId;
      return acc;
    }, {});
  }

  _resolveFinalResult() {
    const answerMap = this._getAnswerMap();
    const matchedRule = (this.config.resultRules || []).find((rule) =>
      Object.entries(rule.when || {}).every(([questionId, responseId]) => answerMap[questionId] === responseId)
    );

    if (matchedRule?.resultKey) return matchedRule.resultKey;

    const lastAnswer = this.answers[this.answers.length - 1];
    const lastQuestion = lastAnswer ? this._getQuestionById(lastAnswer.questionId) : null;
    const lastResponse = lastQuestion?.responses?.find((response) => response.id === lastAnswer?.responseId);

    return lastResponse?.resultKey || this.finalResult || null;
  }
  getResults() {
    const resultKey = this._resolveFinalResult();
    return resultKey ? { [resultKey]: 1 } : {};
  }
  getAnswers() {
    return this.answers.slice();
  }

  _renderCurrent() {
    if (this.mode === 'start') this._renderStart();
    else if (this.mode === 'question') this._renderQuestion(this.index);
    else if (this.mode === 'between') this._renderBetween(this.index);
    else this._renderEnd();
  }

  _setContent(node) {
    const current = this.root.firstElementChild;
    const appendNext = () => {
      node.classList.add('ykq-fade-target', 'ykq-fade-in');
      this.root.append(node);
      node.addEventListener('animationend', () => node.classList.remove('ykq-fade-in'), { once: true });
    };

    if (current) {
      current.classList.add('ykq-fade-target', 'ykq-fade-out');
      current.addEventListener(
        'animationend',
        () => {
          current.remove();
          appendNext();
        },
        { once: true }
      );
    } else {
      appendNext();
    }
  }

  // ---------- Banners ----------
  _renderStart() {
    this.mode = 'start';
    this.index = -1;
    this._saveState();
    const b = this.config.banners.start || {};
    this._setContent(
      this._bannerEl(b, {
        primary: {
          label: b.cta || this.options.locale.startCta,
          onClick: () => {
            this._gotoQuestion(0);
            dataLayerPromise({
              event: 'quiz_interaction',
              section: this.config.id,
              element: 'start',
            });
          },
        },
      })
    );
  }

  _renderEnd() {
    this.mode = 'end';
    this.index = this.config.questions.length; // sentinel
    this._saveState();
    const b = this.config.banners.end || {};
    const onPrimary = () => {
      dataLayerPromise({
        event: 'quiz_interaction',
        section: this.config.id,
        element: 'finish',
      });
      const results = this.getResults();
      const resultKey = Object.keys(results)[0] || this._resolveFinalResult();
      const ev = new CustomEvent('quiz:end', { detail: { results, resultKey, answers: this.getAnswers() } });
      this.root.dispatchEvent(ev);
      if (typeof this.options.onEnd === 'function') this.options.onEnd(resultKey, this.getAnswers(), results);
      // nie czyścimy storage dopóki wywołanie onEnd się nie powiedzie
      this._resetState();
    };
    this._setContent(
      this._bannerEl(b, {
        primary: { label: b.cta || this.options.locale.showResult, onClick: onPrimary },
        secondary: {
          label: this.options.locale.prevCta,
          onClick: () => this._goBack(),
        },
      })
    );
    setTimeout(() => window.scrollTo(0, 0), 300);
  }

  _renderBetween(qIndex) {
    this.mode = 'between';
    this.index = qIndex; // between po pytaniu qIndex
    this._saveState();

    setTimeout(() => window.scrollTo(0, 0), 300);

    const q = this.config.questions[qIndex];
    const b = q && q.banner ? q.banner : null;
    if (!b) {
      // jeśli brak banera – przechodzimy dalej
      const next = qIndex + 1;
      if (next < this.config.questions.length) return this._gotoQuestion(next);
      return this._renderEnd();
    }

    this._setContent(
      this._bannerEl(b, {
        primary: {
          label: b.cta || this.options.locale.nextCta,
          onClick: () => {
            const next = qIndex + 1;
            if (next < this.config.questions.length) this._gotoQuestion(next);
            else this._renderEnd();
          },
        },
        secondary: { label: this.options.locale.prevCta, onClick: () => this._gotoQuestion(qIndex) },
      })
    );
  }

  // ---------- Questions ----------
  _gotoQuestion(i) {
    this._renderQuestion(i);
  }
  _gotoBetween(i) {
    this._renderBetween(i);
  }

  _createProgressBar(currentStep = 1, totalSteps = 4) {
    // Główny wrapper
    const wrapper = document.createElement('div');
    wrapper.className = 'flex items-center gap-3 w-full pt-[30px] pb-5 w-full';

    // Tekst z numerem kroku
    const stepText = document.createElement('div');
    stepText.className = 'text-xs text-gray80 font-semibold font-sans whitespace-nowrap';
    stepText.textContent = `${currentStep} / ${totalSteps}`;

    // Kontener na progress bar
    const progressContainer = document.createElement('div');
    progressContainer.className = 'flex-1 h-1 bg-gray20 rounded-full overflow-hidden';

    // Wypełniony progress
    const progressFill = document.createElement('div');
    progressFill.className = 'h-full bg-gray100 rounded-full transition-all duration-300';
    progressFill.style.width = `${(currentStep / totalSteps) * 100}%`;

    // Składanie elementów
    progressContainer.appendChild(progressFill);
    wrapper.appendChild(stepText);
    wrapper.appendChild(progressContainer);

    return wrapper;
  }

  _renderQuestion(i) {
    if (i < 0 || i >= this.config.questions.length) return;
    this.mode = 'question';
    this.index = i;
    this._saveState();

    window.scrollTo(0, 0);

    console.log('this.config.questions', this.config.questions);

    const q = this.config.questions[i];
    const savedAnswer = this.answers.find((a) => a.questionId === q.id);

    // Card
    const card = document.createElement('section');
    card.className = 'md:rounded-[8px] w-full height-100vh-without-header relative max-w-[405px] bg-[#F5F5F5]';

    const content = document.createElement('div');
    content.className = 'flex flex-col px-5 tab:px-0';

    if (q.image) {
      const img = document.createElement('img');
      img.className =
        q.imageClasses || 'w-full h-[160px] lg:h-auto md:rounded-[8px] object-cover shadow-lg hidden md:block';
      img.src = q.image;
      content.appendChild(img);
    }

    const temp = new Set(this.config.questions.map((q) => q.step));
    const stepsCount = temp.size;

    const progress = this._createProgressBar(q.step || i + 1, stepsCount);
    content.appendChild(progress);

    const h = document.createElement('h2');
    h.className = 'text-xxl font-ivyPresto font-light text-left mb-3 tracking-[0.72px]';
    h.textContent = q.text;
    content.appendChild(h);

    console.log('q.description', q);

    if (q.description) {
      const d = document.createElement('p');
      d.className = 'font-sans text-s leading-[1.45] tracking-[0.24px] mb-5';
      d.textContent = q.description;
      content.appendChild(d);
    }

    const answers = document.createElement('div');
    answers.className = q.type === 'visual' ? 'grid grid-cols-2 gap-4 sm:gap-5 sm:grid-cols-2' : 'grid gap-3';

    q.responses.forEach((resp) => {
      if (q.type === 'visual') {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'text-left border border-neutral-200 rounded-[2px] flex flex-col gap-3 relative';

        const imgWrap = document.createElement('div');
        imgWrap.className = 'img-wrapper h-[110px] bg-neutral-100 rounded relative';
        const img = document.createElement('img');
        img.src = resp.image ? this._getCroppedSrc(resp.image) : '';
        img.alt = resp.text || '';
        img.className = 'w-full h-full object-cover rounded-sm';
        imgWrap.appendChild(img);

        const body = document.createElement('div');
        // body.className = 'absolute bottom-0 left-0 translate-y-full';
        const p = document.createElement('p');
        p.className = 'text-[12px] font-sans font-medium tracking-[0.36px]';
        p.textContent = resp.text || '';
        body.appendChild(p);

        btn.append(imgWrap, body);
        btn.addEventListener('click', () => {
          // tylko zaznacz — zapiszemy dopiero po „Następne”
          this.pending[q.id] = resp.id;
          // podświetl wybór (ring)
          answers.querySelectorAll('button').forEach((el) => el.classList.remove('active-answer'));
          btn.classList.add('active-answer');
          const next = document.querySelector(`.button-next-${q.id}`);
          if (typeof next !== 'undefined') next.disabled = false; // odblokuj „Następne”
        });
        answers.appendChild(btn);

        if (savedAnswer && savedAnswer.responseId === resp.id) {
          btn.click();
        }
      } else {
        const label = document.createElement('label');
        label.className = 'flex items-start gap-3 p-3 cursor-pointer bg-white rounded-[8px]';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = q.id;
        input.value = resp.id;
        input.style.filter = 'grayscale(1)';
        input.className = 'mt-1';
        const p = document.createElement('p');
        p.className = 'text-[13px] font-sans yk-cart-badge-padding';
        p.textContent = resp.text || '';
        label.append(input, p);
        label.addEventListener('click', (e) => {
          if (e.target.tagName !== 'INPUT') input.checked = true;
        });
        label.addEventListener('click', () => {
          const next = document.querySelector(`.button-next-${q.id}`);
          this.pending[q.id] = resp.id; // tylko zaznacz
          if (typeof next !== 'undefined') next.disabled = false; // odblokuj „Następne”
        });
        answers.appendChild(label);

        if (savedAnswer && savedAnswer.responseId === resp.id) {
          label.click();
        }
      }
    });

    content.appendChild(answers);

    // Footer actions
    const footer = document.createElement('div');
    footer.className = 'pt-[40px] flex items-center justify-between gap-3 pb-[30px] bg-[#F5F5F5] sticky bottom-0';
    footer.style.background =
      'linear-gradient(180deg,rgba(255, 255, 255, 0) 0%, rgba(245, 245, 245, 0.85) 25%, rgba(245, 245, 245, 1) 100%)';
    const back = document.createElement('button');
    back.type = 'button';
    back.className =
      'btn btn--secondary bg-white button button--full-width h-[40px] px-5 py-2.5 justify-center w-full gap-3 rounded disabled:text-gray20 text-black relative button--primary';
    back.textContent = this.options.locale.prevCta;
    back.disabled = i === 0;

    back.addEventListener('click', () => {
      dataLayerPromise({
        event: 'quiz_interaction',
        section: this.config.id,
        element: `q_${i + 1}`,
        element_value: 'previous',
      });
      this._goBack();
    });

    const next = document.createElement('button');
    next.type = 'button';
    next.className = `btn btn--primary-2 button button--full-width h-[40px] text-[12px] px-5 py-2.5 justify-center w-full gap-3 rounded relative button--primary button-next-${q.id}`;

    next.textContent =
      i === this.config.questions.length - 1 ? this.options.locale.finishCta : this.options.locale.nextCta;
    next.disabled = !this.answers.find((a) => a.questionId === q.id);
    next.addEventListener('click', () => {
      dataLayerPromise({
        event: 'quiz_interaction',
        section: this.config.id,
        element: `q_${i + 1}`,
        element_value: 'next',
      });
      // ustalamy wybraną odpowiedź: preferuj pending; jeśli brak — weź zapisaną (gdy wracamy „cofnij”)
      let resp = null;
      if (this.pending[q.id] != null) {
        resp = q.responses.find((r) => String(r.id) === String(this.pending[q.id]));
      } else {
        const saved = this.answers.find((a) => a.questionId === q.id);
        if (saved) resp = q.responses.find((r) => String(r.id) === String(saved.responseId));
      }
      if (!resp) return; // nic nie wybrane -> nic nie rób
      this._saveAnswer(q, resp); // dopiero teraz zapis
      this._afterAnswer(q, resp, i); // baner po pytaniu / kolejne pytanie / koniec
    });

    if (i === 0) {
      footer.append(next);
    } else {
      footer.append(back, next);
    }

    content.appendChild(footer);

    card.appendChild(content);

    this._setContent(card);

    this.root.addEventListener('change', (e) => {
      if (e.target && e.target.name === q.id) next.disabled = false;
    });
  }

  _goBack() {
    if (this.mode === 'question') {
      const currentQuestion = this.config.questions[this.index];
      const previousQuestionId = currentQuestion?.previousQuestionId;

      if (previousQuestionId) {
        const prevIndex = this._getQuestionIndexById(previousQuestionId);
        if (prevIndex >= 0) return this._renderQuestion(prevIndex);
      }

      const prev = this.index - 1;
      if (prev >= 0) this._renderQuestion(prev);
      else this._renderStart();
    } else if (this.mode === 'between') {
      // wracamy do pytania, po którym pokazaliśmy baner
      this._renderQuestion(this.index);
    } else if (this.mode === 'end') {
      const lastAnswer = this.answers[this.answers.length - 1];
      const prevIndex = lastAnswer
        ? this._getQuestionIndexById(lastAnswer.questionId)
        : this.config.questions.length - 1;
      this._renderQuestion(prevIndex >= 0 ? prevIndex : this.config.questions.length - 1);
    }
  }

  _saveAnswer(question, response) {
    const i = this.answers.findIndex((a) => a.questionId === question.id);
    const entry = { questionId: question.id, responseId: response.id, type: response.type || null };
    if (i >= 0) this.answers[i] = entry;
    else this.answers.push(entry);

    const currentStep = question.step || 1;
    this.answers = this.answers.filter((answer) => {
      if (answer.questionId === question.id) return true;
      const relatedQuestion = this._getQuestionById(answer.questionId);
      return relatedQuestion && (relatedQuestion.step || 1) < currentStep + 1;
    });

    this.finalResult = this._resolveFinalResult();
    this._saveState();
    delete this.pending[question.id]; // po zapisie czyścimy „pending”
  }

  _afterAnswer(question, response, qIndex) {
    const nextQuestionId = response.nextQuestionId;
    const hasBanner = !!question?.banner;

    if (response.resultKey) {
      this.finalResult = response.resultKey;
      if (hasBanner) return this._renderBetween(qIndex);
      return this._renderEnd();
    }

    if (nextQuestionId) {
      this.finalResult = this._resolveFinalResult();
      if (hasBanner) return this._renderBetween(qIndex);
      const nextIndex = this._getQuestionIndexById(nextQuestionId);
      if (nextIndex >= 0) return this._renderQuestion(nextIndex);
    }

    if (hasBanner) this._renderBetween(qIndex);
    else {
      const next = qIndex + 1;
      if (next < this.config.questions.length) this._renderQuestion(next);
      else this._renderEnd();
    }
  }

  _getCroppedSrc(src, width = 960) {
    const char = src.includes('?') ? '&' : '?';
    return src + `${char}crop=center&width=${width}&fit=max`;
  }

  // ---------- Shared UI ----------
  _bannerEl(data, actions = {}) {
    const { heading, headingClasses, title, titleClasses, subtitle, subtitleClasses, image } = data || {};
    const wrap = document.createElement('section');
    wrap.className =
      'overflow-hidden sm:shadow-lg w-full height-100vh-without-header sm:rounded-[8px] overflow-auto relative bg-[#F5F5F5]';

    const media = document.createElement('div');
    media.className = 'absolute top-0 left-0 bottom-0 right-0 h-full w-full flex items-center justify-center';
    if (image) {
      const img = document.createElement('img');
      img.src = this._getCroppedSrc(image);
      img.alt = title || 'baner';
      img.className = 'w-full h-full object-cover rounded-sm';
      media.appendChild(img);
    } else {
      const ph = document.createElement('div');
      ph.className = 'text-neutral-400';
      ph.textContent = '—';
      media.appendChild(ph);
    }

    const content = document.createElement('div');
    content.className = 'p-6 sm:p-8 absolute bottom-10 left-0 right-0 flex flex-col justify-center items-center';

    if (heading) {
      const headingEl = document.createElement('h2');
      if (headingClasses) {
        headingEl.className = headingClasses;
      } else {
        headingEl.className =
          'font-ivyPresto text-2xl font-light text-center tracking-[0.72px] leading-[1.2] text-white mb-3 whitespace-pre-line';
      }
      headingEl.innerHTML = heading || '';
      content.append(headingEl);
    }

    const h = document.createElement('h2');
    if (titleClasses) {
      h.className = titleClasses;
    } else {
      h.className = 'font-sans text-[11px] font-semibold text-center tracking-[1.78px] uppercase text-black mb-3';
    }
    h.innerHTML = title || '';
    const p = document.createElement('p');
    if (subtitleClasses) {
      p.className = subtitleClasses;
    } else {
      p.className = 'font-ivyPresto text-lg text-center text-black font-light tracking-[0.72px] whitespace-pre-line';
    }
    p.innerHTML = subtitle || '';

    const ctaWrap = document.createElement('div');
    ctaWrap.className = 'mt-6 flex items-center gap-3';

    if (actions.primary) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className =
        'btn btn--primary-2 button button--full-width h-[40px] text-[12px] px-5 py-2.5 justify-center w-full gap-3 rounded relative button--primary';
      btn.textContent = actions.primary.label;
      btn.addEventListener('click', actions.primary.onClick);
      ctaWrap.appendChild(btn);
    }

    content.append(h);
    if (subtitle) content.append(p);
    content.append(ctaWrap);

    wrap.append(media, content);
    return wrap;
  }
}

customElements.define('yk-quiz', YKQuiz);
